import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Plus, Check, LayoutDashboard, Globe } from 'lucide-react';
import { apiWorker } from '../../api/worker';
import { Badge } from '../ui/Badge';
import { cn } from '../../lib/utils';
import { useTenant } from '../../contexts/TenantContext';
import { useCurrentUser } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';

interface Membership {
  id: string;
  nodeName: string;
  nodeSlug: string;
  role: string;
  unreadCount: number;
  parentNodeId?: string;
}

export const OrgSwitcher = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { slug: currentSlug, isGlobal, isUniversal, clearActiveOrg, switchOrg } = useTenant();
  const { isSuperAdmin, isSystemAdmin } = usePermissions();

  const { user, memberships: rawMembers } = useCurrentUser();
  const navigate = useNavigate();

  // Sort memberships hierarchically
  const memberships = React.useMemo(() => {
    if (!rawMembers || rawMembers.length === 0) return [];
    
    // Filter out invalid/orphaned memberships from deleted nodes
    const validRawMembers = rawMembers.filter(
      m => m.nodeSlug && m.nodeSlug !== 'unknown' && m.nodeName && m.nodeName !== 'Unknown Organization'
    );
    if (validRawMembers.length === 0) return [];

    // Safety check: ensure memberships are unique to prevent trivial cycles
    const rawMembersMap = new Map();
    validRawMembers.forEach(m => rawMembersMap.set(m.id, m));
    
    const roots = validRawMembers.filter(m => !m.parentNodeId || !rawMembersMap.has(m.parentNodeId));
    const result: Membership[] = [];
    const processedIds = new Set<string>();

    const processNode = (node: Membership, depth = 0) => {
      // Depth limit and cycle detection
      if (depth > 20 || processedIds.has(node.id)) return;
      
      processedIds.add(node.id);
      result.push(node);
      
      const children = validRawMembers.filter(m => m.parentNodeId === node.id);
      children.forEach(child => processNode(child, depth + 1));
    };

    roots.forEach(root => processNode(root));
    
    // Add any memberships that were missed (e.g. detached cycles or deep trees)
    // to ensure no data is lost from the UI
    validRawMembers.forEach(m => {
      if (!processedIds.has(m.id)) {
        result.push(m);
        processedIds.add(m.id);
      }
    });

    return result;
  }, [rawMembers]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSwitch = (slug: string) => {
    if (slug === currentSlug) {
      setIsOpen(false);
      return;
    }
    setIsOpen(false);
    switchOrg(slug, '/dashboard');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-xs font-bold font-mono uppercase tracking-widest text-black bg-white border-2 border-black rounded-none px-4 py-2 cursor-pointer hover:bg-[#FFD600] hover:text-black transition-colors shadow-[2px_2px_0px_#000000] dark:bg-slate-900 dark:text-white dark:border-white"
      >
        Switch Org
        <ChevronDown size={16} className={cn('transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-4 w-72 bg-white rounded-none shadow-[8px_8px_0px_#000000] border-4 border-black py-2 z-[100] dark:bg-black dark:border-white dark:shadow-[8px_8px_0px_#FFFFFF]">
          <div className="px-4 py-3 border-b-4 border-black mb-1">
            <p className="text-[10px] font-black uppercase tracking-widest text-black dark:text-white font-mono">Your Organizations</p>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {(isSuperAdmin || isSystemAdmin || isGlobal || (!isUniversal && !memberships.some(m => m.nodeSlug === currentSlug))) && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  clearActiveOrg(isSuperAdmin || isSystemAdmin ? '/system-admin' : '/dashboard');
                }}
                className="w-full flex items-center gap-4 px-4 py-3 hover:bg-[#FFD600] hover:text-black transition-colors text-left group border-b-2 border-black dark:border-white dark:hover:bg-[#FFD600] dark:hover:text-black"
              >
                <div className={cn(
                  'w-10 h-10 border-2 border-black flex items-center justify-center text-xs font-bold text-white flex-shrink-0 bg-black',
                  isGlobal && !isUniversal && 'bg-[#0044FF]'
                )}>
                  <Globe size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-base font-black font-display uppercase tracking-tight text-black dark:text-white group-hover:text-black truncate">
                      Platform Global
                    </p>
                    {isGlobal && !isUniversal && <Check size={16} className="text-[#0044FF]" />}
                  </div>
                  <p className="text-[10px] text-gray-500 font-bold font-mono uppercase tracking-wider group-hover:text-black">
                    {isSuperAdmin || isSystemAdmin ? 'System Admin Root' : 'Exit to Platform'}
                  </p>
                </div>
              </button>
            )}

            <button
              onClick={() => handleSwitch('universal')}
              className="w-full flex items-center gap-4 px-4 py-3 hover:bg-[#FFD600] hover:text-black transition-colors text-left group border-b-2 border-black dark:border-white dark:hover:bg-[#FFD600] dark:hover:text-black"
            >
              <div className={cn(
                'w-10 h-10 border-2 border-black flex items-center justify-center text-xs font-bold text-white flex-shrink-0 bg-black',
                currentSlug === 'universal' && 'bg-[#0044FF]'
              )}>
                <LayoutDashboard size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-base font-black font-display uppercase tracking-tight text-black dark:text-white group-hover:text-black truncate">Universal View</p>
                  {currentSlug === 'universal' && <Check size={16} className="text-[#0044FF]" />}
                </div>
                <p className="text-[10px] text-gray-500 font-bold font-mono uppercase tracking-wider group-hover:text-black">All your organizations</p>
              </div>
            </button>

            {memberships.length > 0 ? (
              memberships.map((org) => {
                const isChild = !!org.parentNodeId && rawMembers.some(m => m.id === org.parentNodeId);
                return (
                  <button
                    key={org.id}
                    onClick={() => handleSwitch(org.nodeSlug)}
                    className={cn(
                      "w-full flex items-center gap-4 px-4 py-3 hover:bg-[#FFD600] hover:text-black transition-colors text-left group relative border-b-2 border-black dark:border-white dark:hover:bg-[#FFD600] dark:hover:text-black",
                      isChild && "pl-12"
                    )}
                  >
                    {isChild && (
                      <div className="absolute left-6 top-0 bottom-0 w-1 bg-gray-200">
                        <div className="absolute left-0 top-1/2 w-4 h-1 bg-gray-200" />
                      </div>
                    )}
                    <div className={cn(
                      'w-10 h-10 border-2 border-black flex items-center justify-center text-xs font-bold font-mono text-black flex-shrink-0',
                      org.nodeSlug === currentSlug ? 'bg-[#0044FF] text-white' : 'bg-white group-hover:bg-white',
                      isChild && 'w-8 h-8 text-[10px]'
                    )}>
                      {(org.nodeName || '').substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={cn(
                          "text-base font-black font-display uppercase tracking-tight text-black dark:text-white truncate group-hover:text-black",
                          isChild && "text-sm"
                        )}>{org.nodeName}</p>
                        {org.nodeSlug === currentSlug && <Check size={16} className="text-[#0044FF]" />}
                      </div>
                      <p className="text-[10px] text-gray-500 font-bold font-mono uppercase tracking-widest group-hover:text-black">{org.role}</p>
                    </div>
                    {org.unreadCount > 0 && (
                      <Badge className="bg-[#0044FF] text-white border-2 border-black rounded-none px-2 font-mono text-[10px]">
                        {org.unreadCount}
                      </Badge>
                    )}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-4 text-center">
                <p className="text-xs font-mono font-bold uppercase text-gray-500">
                  {isSuperAdmin || isSystemAdmin ? 'No enrolled hubs (Admin)' : 'No other orgs'}
                </p>
              </div>
            )}
          </div>

          <div className="mt-2 pt-2 border-t-4 border-black px-4">
            <button 
              onClick={() => {
                setIsOpen(false);
                navigate('/create-org');
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-black bg-black text-white rounded-none text-xs font-bold font-mono uppercase tracking-widest hover:bg-[#0044FF] transition-colors"
            >
              <Plus size={16} />
              Add or join org
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
